import { useRef, useState } from 'react';
import { IconButton } from 'components/buttons/IconButton';
import { HasOpenState } from 'hooks/HasOpenState';
import { IActionLinkItem } from 'components/ActionLinks/ActionLinkModels';
import {
  addActionWithEvents,
  removeStepWithEvents,
} from '@decisions/server/DecisionsFramework/Design/Flow/Service/Edit/TruthTable/TruthTableEditService';

import {
  moveTruthTableRowDown,
  moveTruthTableRowUp,
  getEditContextForTruthTableRow,
} from '@decisions/server/DecisionsFramework/Design/Flow/Service/RuleEditService';
import { ShowContextEditorLayerCallback } from 'domains/designers/TruthTable/TruthTable';
import { HandleNotificationsCallback } from '../TruthTableUtils';
import './SettingsCell.less';
import { SettingsColumn } from '../columns';
import { Cell } from './Cell';
import { conditionalArray } from 'utils/ArrayUtils';
import { RowSettings } from '@decisions/server/DecisionsFramework/Design/Flow/Service/TruthTable/RowSettings';
import classNames from 'classnames';
import { ContextMenu } from '../components';

interface ISettingsCellProps {
  ruleSessionId: string;
  rowId: string;
  handleNotifications: HandleNotificationsCallback;
  showContextEditorLayer: ShowContextEditorLayerCallback;
  onAddRowBefore: () => void;
  onAddRowAfter: () => void;
  getRowSettings: (rowId: string) => RowSettings | Promise<RowSettings>;
}

/**
 * Shows row actions by click.
 *
 * Copyright (c) 2020 Decisions All Rights Reserved
 */
export function SettingsCell({
  rowId,
  ruleSessionId,
  handleNotifications,
  showContextEditorLayer,
  onAddRowBefore,
  onAddRowAfter,
  getRowSettings,
}: ISettingsCellProps) {
  const { isOpen, setOpen, setClosed } = HasOpenState();
  const settingButton = useRef<HTMLButtonElement>(null);
  const [settings, setSettings] = useState<RowSettings | null>(null);
  const [isLoadingActions, setLoadingActions] = useState(false);

  const showContextMenu = async () => {
    setOpen();

    try {
      setLoadingActions(true);

      const rowSettings = await getRowSettings(rowId);
      setSettings(rowSettings);
    } finally {
      setLoadingActions(false);
    }
  };

  const handleAddAction = () => {
    setClosed();
    addActionWithEvents({
      ruleSessionId,
      parentGroupId: rowId,
    }).then(data => {
      const notifications = data.map(item => item.OutputValue);
      handleNotifications(notifications);
    });
  };

  const handleDeleteRow = () => {
    setClosed();
    removeStepWithEvents({
      ruleSessionId,
      ruleStepId: rowId,
    }).then(data => {
      const notifications = data.map(item => item.OutputValue);
      handleNotifications(notifications);
    });
  };

  const handleMoveDown = () => {
    setClosed();
    moveTruthTableRowDown({
      ruleSessionId,
      rowId: rowId,
    }).then(data => {
      const notifications = data.map(item => item.OutputValue);
      handleNotifications(notifications);
    });
  };

  const handleMoveUp = () => {
    setClosed();
    moveTruthTableRowUp({
      ruleSessionId,
      rowId: rowId,
    }).then(data => {
      const notifications = data.map(item => item.OutputValue);
      handleNotifications(notifications);
    });
  };

  const handleEditColumns = () => {
    setClosed();
    showContextEditorLayer({
      getContext: () =>
        getEditContextForTruthTableRow({
          ruleStepId: rowId,
          ruleSessionId: ruleSessionId,
          startWithMappings: false,
        }),
      target: settingButton.current as HTMLElement,
    });
  };

  return (
    <SettingsColumn>
      <Cell className="flex-centering tt-setting-cell">
        <IconButton
          title="Row Actions"
          aria-label="Row Actions"
          ref={settingButton}
          disabled={isLoadingActions}
          iconClassName={classNames('tt-setting-cell__icon', {
            'tt-setting-cell__icon--active': isOpen,
          })}
          onClick={showContextMenu}
        />
        <ContextMenu
          isOpen={isOpen}
          onClose={() => {
            setSettings(null);
            setClosed();
          }}
          sourceRef={settingButton}
          placement="right"
          isLoading={isLoadingActions}
          actions={conditionalArray<IActionLinkItem>(
            [
              {
                label: 'Add Action',
                value: 'Add Action',
                onClick: handleAddAction,
              },
              settings?.AllowActions === true,
            ],
            [
              {
                label: 'Delete',
                value: 'Delete',
                onClick: handleDeleteRow,
              },
              settings?.CanDelete === true,
            ],
            [
              {
                label: 'Move Down',
                value: 'Move Down',
                onClick: handleMoveDown,
              },
              settings?.CanMoveDown === true,
            ],
            [
              {
                label: 'Move Up',
                value: 'Move Up',
                onClick: handleMoveUp,
              },
              settings?.CanMoveUp === true,
            ],
            {
              label: 'Add Row Before',
              value: 'Add Row Before',
              onClick: () => {
                setClosed();
                onAddRowBefore();
              },
            },
            {
              label: 'Add Row After',
              value: 'Add Row After',
              onClick: () => {
                setClosed();
                onAddRowAfter();
              },
            },
            {
              //TODO alex: need to add separator before "Edit Columns" action, but "ActionLinks" doesn't support it yet
              label: 'Edit Columns',
              value: 'Edit Columns',
              onClick: handleEditColumns,
            }
          )}
        />
      </Cell>
    </SettingsColumn>
  );
}
